<template>
  <!-- Table Container Card -->
  <b-card>

    <validation-observer ref="trialBalanceRules">
      <b-form @submit.prevent="handleSubmit">
        <b-row class="match-height">
          <!-- Branch -->
          <b-col md="3">
            <SelectField
              id="branch"
              v-model="trialBalanceFormData.branch"
              :label="$t('Branch')"
              :options="branchOptions"
              :reduce="option => option"
              :error-name="$t('Branch')"
              item="label"
              :placeholder="$t('Select')"
            />
          </b-col>
          <!-- Branch -->

          <!-- Level -->
          <b-col md="2">
            <SelectField
              id="level"
              v-model="trialBalanceFormData.level"
              :label="$t('Level')"
              :options="translatedLevelOptions"
              :reduce="option => option.value"
              :error-name="$t('Level')"
              rules="required"
              item="label"
              :placeholder="$t('Select')"
            />
          </b-col>
          <!-- Level -->
          <b-col
            md="3"
            class="mt-2 align-items-center d-flex justify-content-center"
          >
            <SwitchButton
              v-model="trialBalanceFormData.isCurrentFinancialDate"
              :label="$t('Use Current Financial Date')"
              name="use-current-financial-date"
              :readonly="isReadonly"
              @input="setUseCurrentFinancialDate(trialBalanceFormData.isCurrentFinancialDate)"
            />
          </b-col>

          <b-col
            md="2"
            class="mt-2 current-year"
          >
            <SwitchButton
              v-model="trialBalanceFormData.ignoreZeroBalances"
              :label="$t('Ignore Zero Balances?')"
              name="ignore-zero-balances"
              :readonly="isReadonly"
            />
          </b-col>

          <!-- Financial Date-->
          <b-col md="2">
            <DatePickerField
              id="financial-date"
              v-model="trialBalanceFormData.financialDate"
              rules="required"
              :min-date="new Date()"
              :label="$t('Financial Date')"
              :error-name="$t('Financial Date')"
              :config="datePickerConfig"
              :placeholder="$t('Select Date')"
              :disabled="isDisabledText === true"
            />
          </b-col>
          <!--  Financial Date -->

          <b-col
            md="2"
            class="mt-2"
          >
            <Button
              :label="$t('Generate Report')"
              :loading="isLoading"
              :disabled="isDisabled"
              variant="primary"
              size="md"
              @click="handleSubmit"
            />
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        />
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-dropdown
              v-if="reportData.length && getUserPermission('EXPORT_TRIAL_BALANCE_REPORT')"
              id="dropdown-1"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              :text="$t('Export')"
              variant="primary"
            >
              <b-dropdown-item @click="csvExport(csvData)">
                {{ $t('CSV') }}
              </b-dropdown-item>
              <b-dropdown-item @click="excelExport(csvData)">
                {{ $t('Excel') }}
              </b-dropdown-item>
              <b-dropdown-item @click="pdfExport">
                {{ $t('PDF') }}
              </b-dropdown-item>
              <b-dropdown-item @click="printData">
                {{ $t('Print') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </b-col>

      </b-row>

    </div>

    <div id="trial-balance-report-printer">
      <b-card
        id="prints"
        class="header-position"
      >
        <h2>{{ userDetails.institutionName }}</h2>
        <h4> {{ $t('Trial Balance Report') }} </h4>
        <h4>{{ trialBalanceFormData.isCurrentFinancialDate ? 'Date: All' : `As at ${trialBalanceFormData.financialDate}` }}</h4>
        <h4>{{ trialBalanceFormData.branch ? trialBalanceFormData.branch.label : 'Branch: All' }}</h4>
        <h4> {{ $t('Level') }} {{ trialBalanceFormData.level }}</h4>
      </b-card>
      <b-table
        v-if="reportData.length"
        ref="glTableRef"
        :items="reportData"
        :busy="isBusy"
        :per-page="3000"
        :current-page="currentPage"
        responsive
        :fields="translatedTableHeaders"
        primary-key="id"
        show-empty
        :empty-text="$t('No records available')"
        class="position-relative"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner
              class="align-middle"
              variant="primary"
            />
          </div>
        </template>
        <template #cell(debit)="data">
          <span class="text-nowrap">
            {{ toBalance(data.value) }}
          </span>
        </template>
        <template #cell(credit)="data">
          <span class="text-nowrap">
            {{ toBalance(data.value) }}
          </span>
        </template>

      </b-table>
    </div>

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BForm, BCol, BTable, BSpinner, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions, mapGetters } from 'vuex'
import Ripple from 'vue-ripple-directive'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import * as FileSaver from 'file-saver'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toYearWithDate, toCustomDate } from '@/helpers/formatDate'
import { toBalance, formatAmount, toFormatAmount } from '@/helpers/formatAmount'
import 'jspdf-autotable'
import { reportDataDetail } from '../../../../helpers/data'
import SwitchButton from '../../../components/switch/Switch.vue'
import SelectField from '../../../forms/SelectField.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import Button from '../../../components/button/Button.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BSpinner,
    BTable,
    BDropdown,
    BForm,
    Button,
    BDropdownItem,
    SelectField,
    SwitchButton,
    DatePickerField,
    ValidationObserver,
    FullScreenSpinner,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toBalance,
      toFormatAmount,
      isBusy: false,
      isDisabledText: false,
      tableHeaders: [
        { key: 'description', label: 'Description' },
        { key: 'code', label: 'Code' },
        { key: 'debit', label: 'Debit' },
        { key: 'credit', label: 'Credit' },
      ],
      levelOptions: [
        { label: 'Level 1', value: 1 },
        { label: 'Level 2', value: 2 },
        { label: 'Level 3', value: 3 },
        { label: 'Level 4', value: 4 },
      ],
      required,
      isLoading: false,
      isDisabled: false,
      showSpinner: false,
      reportData: [],
      reportInfo: reportDataDetail,
      trialBalanceFormData: {
        branch: '',
        level: this.$t('Level 1'),
        financialDate: null,
        isCurrentFinancialDate: true,
        ignoreZeroBalances: true,
      },
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        maxDate: 'today',
        defaultDate: '',
      },
    }
  },
  computed: {
    csvData() {
      return this.reportData.map(item => ({
        Description: item.description,
        Code: item.code,
        Debit: item.debit ? this.toAmount(item.debit) : 0,
        Credit: item.credit ? this.toAmount(item.credit) : 0,
      }))
    },

    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedLevelOptions() {
      return this.levelOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  methods: {
    ...mapActions({
      getTrialBalance: 'reports/getTrialBalance',
      downloadTrialBalanceReport: 'reports/downloadTrialBalanceReport',
    }),
    setUseCurrentFinancialDate(dataValue) {
      if (dataValue === false) {
        this.isDisabledText = false
        this.trialBalanceFormData.financialDate = ''
      } else {
        this.isDisabledText = true
        this.trialBalanceFormData.financialDate = ''
      }
    },
    async fetchTrialBalance() {
      try {
        this.showSpinner = true
        if (this.trialBalanceFormData.level === 'Level 1') {
          this.trialBalanceFormData.level = 1
        }
        const response = await this.getTrialBalance({
          ...this.trialBalanceFormData,
          branchCode: this.trialBalanceFormData.branch ? this.trialBalanceFormData.branch.value : '',
          financialDate: this.trialBalanceFormData.financialDate ? toYearWithDate(this.trialBalanceFormData.financialDate) : '',
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        this.showSpinner = true
        if (this.trialBalanceFormData.level === 'Level 1') {
          this.trialBalanceFormData.level = 1
        }
        const response = await this.downloadTrialBalanceReport({
          ...this.trialBalanceFormData,
          branchCode: this.trialBalanceFormData.branch ? this.trialBalanceFormData.branch.value : '',
          financialDate: this.trialBalanceFormData.financialDate ? toYearWithDate(this.trialBalanceFormData.financialDate) : '',
          fileExportFormat: 1,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
          FileSaver.saveAs(blob, 'Trial Balance Report.csv')
          this.alertSuccess({ text: this.$t('Your request to download Trial Balance Report was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
        this.showSpinner = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        this.showSpinner = true
        if (this.trialBalanceFormData.level === 'Level 1') {
          this.trialBalanceFormData.level = 1
        }
        const response = await this.downloadTrialBalanceReport({
          ...this.trialBalanceFormData,
          branchCode: this.trialBalanceFormData.branch ? this.trialBalanceFormData.branch.value : '',
          financialDate: this.trialBalanceFormData.financialDate ? toYearWithDate(this.trialBalanceFormData.financialDate) : '',
          fileExportFormat: 0,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
          FileSaver.saveAs(blob, 'Trial Balance Report.xlsx')
          this.alertSuccess({ text: this.$t('Your request to download Trial Balance Report was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
        this.showSpinner = false
      }
    },
    async pdfExport() {
      try {
        this.isBusy = true
        this.showSpinner = true
        if (this.trialBalanceFormData.level === 'Level 1') {
          this.trialBalanceFormData.level = 1
        }
        const response = await this.downloadTrialBalanceReport({
          ...this.trialBalanceFormData,
          branchCode: this.trialBalanceFormData.branch ? this.trialBalanceFormData.branch.value : '',
          financialDate: this.trialBalanceFormData.financialDate ? toYearWithDate(this.trialBalanceFormData.financialDate) : '',
          fileExportFormat: 2,
        })
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/pdf' })
          FileSaver.saveAs(blob, 'Trial Balance Report.pdf')
          this.alertSuccess({ text: this.$t('Your request to download Trial Balance Report was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
        this.showSpinner = false
      }
    },
    printData() {
      window.print()
    },
    handleSubmit() {
      if (this.trialBalanceFormData.isCurrentFinancialDate === false) {
        this.$refs.trialBalanceRules.validate().then(async success => {
          if (success) {
            this.fetchTrialBalance()
          }
        })
      } else {
        this.fetchTrialBalance()
      }
    },
    formatCustomDate: toCustomDate,
    toAmount: formatAmount,
  },
}
</script>

<style lang="scss">
   @import '@core/scss/vue/libs/vue-select.scss';
      @media screen {
  #prints {
    display: none;
   }
}

@media print {
   html, body {
     height: 99%;
     page-break-after: avoid !important;
     page-break-before: avoid !important;
     visibility:hidden;
  }
  #trial-balance-report-printer, #trial-balance-report-printer * {
    visibility: visible; border: none;
  }
  .header-position {
    text-align: center;
    font-weight: 700;
    margin-top: -60px !important;
  }
  .table-position {
     margin-top: -60px !important;
  }
  #prints, #prints * {
    visibility:visible;
  }
  #prints {
    position:absolute;
    left:0;
    top:0;
    right: 0;
  }
  img {
  max-width: 50%;
  height: auto;
}
}
</style>
